import React from "react";
import { Tooltip } from "@mui/material";
import { Clock, Info } from "lucide-react";

import TimeZonePicker from "@/components/common/TimeZonePicker";
import {
  useViewerTimeZone,
  useViewerTimeZonePreference,
} from "@/hooks/useViewerTimeZone";
import { timeZoneDisplayName } from "@/utils/temporal";
import { FOLLOW_BROWSER, cachedBrowserTimeZone } from "@/utils/viewerTimeZone";

/**
 * The zone a viewer reads the app in (#11055).
 *
 * Lives in the account menu at the nav's foot (`SidebarAccount`) rather than a
 * settings page because it is the kind
 * of thing someone fixes the morning after a flight, from wherever they happen
 * to be in the app, without losing what they were doing.
 *
 * The control is the shared `TimeZonePicker` -- searchable, one row per zone,
 * the same one the occurrence editor and the admin org editor use. A native
 * `<select>` would have been hundreds of unsearchable rows.
 *
 * The one thing this surface adds is the row at the top, tagged "where you are
 * now": it is also the way back, since picking it clears the pin rather than
 * pinning that name, so a traveller does not have to find their own zone in
 * the list to start following the browser again.
 */

const currentTimeIn = (zone: string): string => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date());
  } catch {
    return "";
  }
};

const ViewerTimeZoneSelect: React.FC = () => {
  const [preference, setPreference] = useViewerTimeZonePreference();
  const effectiveZone = useViewerTimeZone();
  const browserZone = cachedBrowserTimeZone();
  const localTime = currentTimeIn(effectiveZone);

  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-[#6E7480]">
        <Clock className="w-3.5 h-3.5" />
        Time zone
        <Tooltip
          title="The zone this app shows you times in, and the one it tells the assistant you are in. It defaults to wherever your browser says you are; pin a zone if you would rather stay on another one — travelling, or working to someone else's calendar. It is remembered on this device only and changes nothing for your colleagues."
          arrow
        >
          <Info className="w-3.5 h-3.5 text-gray-400 shrink-0 cursor-pointer" />
        </Tooltip>
      </div>

      <TimeZonePicker
        value={preference.kind === "fixed" ? preference.zone : null}
        onChange={(zone) =>
          setPreference(zone ? { kind: "fixed", zone } : FOLLOW_BROWSER)
        }
        ariaLabel="Time zone"
        unsetLabel={
          browserZone ? timeZoneDisplayName(browserZone) : "Follow this device"
        }
        unsetSearchZone={browserZone ?? undefined}
        unsetHint={browserZone ? "Where you are now" : undefined}
        compact
      />

      {/* The time alone: the field above already names the zone. */}
      {localTime && (
        <div className="mt-1.5 font-mono text-[11px] text-[#6E7480]">
          Currently {localTime}
        </div>
      )}
    </div>
  );
};

export default ViewerTimeZoneSelect;
